"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ScanLine, CheckCircle2, UserCheck } from "lucide-react";

const STEPS = [
  {
    icon: ScanLine,
    title: { en: "Point", es: "Apuntas" },
    sub: { en: "Doorman raises the iPhone. Camera opens at scan view.", es: "El doorman levanta el iPhone. Cámara abre en modo scan." },
    time: "0.0s",
  },
  {
    icon: CheckCircle2,
    title: { en: "Validate", es: "Valida" },
    sub: { en: "QR reads and is checked against the event's tickets. Valid, not duplicated.", es: "El QR se lee y se valida contra los tickets del evento. Válido, no duplicado." },
    time: "0.2s",
  },
  {
    icon: UserCheck,
    title: { en: "In", es: "Adentro" },
    sub: { en: "Green checkmark, guest name and tier on screen.", es: "Checkmark verde, nombre y tier en pantalla." },
    time: "0.4s",
  },
];

export default function QrDoorScanScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Scan flow", "Flujo de scan")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Point. Validate. ", "Apunta. Valida. ")}
            <span className="text-white/30">{t("In.", "Adentro.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Seconds from raised phone to guest moving past the rope. Name and tier right on the screen. No paper list, no scribbled name spelling.",
              "Segundos desde que se levanta el phone hasta que el invitado pasa la cuerda. Nombre y tier en la pantalla. Sin lista en papel, sin nombre garabateado."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-6 relative"
              >
                {/* Time chip */}
                <div className="absolute top-5 right-5 text-[10px] font-black tabular-nums" style={{ color: "#F472B6" }}>
                  {step.time}
                </div>

                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(124,58,237,0.2)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#C4B5FD" }} />
                </div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-1">
                  {t("Step", "Paso")} {i + 1}
                </div>
                <div className="text-[22px] font-black text-white mb-3 tracking-tight">
                  {t(step.title.en, step.title.es)}
                </div>
                <div className="text-[13px] font-medium text-white/55 leading-relaxed">
                  {t(step.sub.en, step.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
